import React, { useEffect, useState, useCallback } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import Column from './Column';
import {
    Card,
    CardBody,
} from 'reactstrap';
import { AxiosAPI } from '../../../../../../../helpers/AxiosAPI';
import {
    putAPI_GuestMessagingReservationStatus_Update
} from '../../../../../../../constants/APIs';
import { useSelector } from 'react-redux';
import { NotificationManager } from '../../../../../../../components/common/react-notifications';

const ALLOWED_MOVES = {
    futureReservation: ['arrived', 'departed'],
    arrivingSoon: ['arrived', 'departed'],
    arrived: ['departed', 'arrivingSoon', 'futureReservation'],
    departed: ['arrived', 'arrivingSoon', 'futureReservation'],
};

const columnOrder = [
    { id: 'futureReservation', title: 'Future Reservation' },
    { id: 'arrivingSoon', title: 'Arriving Soon' },
    { id: 'arrived', title: 'Arrived' },
    { id: 'departed', title: 'Departed' },
];

const ReservationKanbanBoard = ({ data = [], loading = false, onRefresh = () => { } }) => {

    const { userName } = useSelector(({ settings }) => ({
        userName: settings.userName,
    }));

    const buildColumns = useCallback((rows) => {
        const base = columnOrder.reduce((acc, column) => {
            acc[column.id] = [];
            return acc;
        }, {});

        rows.forEach((row, index) => {
            if (!row) return;
            const columnId = row.kanbanStatus || 'futureReservation';

            base[columnId].push({
                id: row.id ? String(row.id) : `reservation-${index}`,
                row,
                isDragDisabled: row.dataSource !== "CsvUploadAndManual",
            });
        });

        return base;
    }, []);

    const [columns, setColumns] = useState(() => buildColumns(Array.isArray(data) ? data : []));

    useEffect(() => {
        setColumns(buildColumns(Array.isArray(data) ? data : []));
    }, [data, buildColumns]);


    const onDragEnd = async (result) => {
        const { source, destination } = result;
        if (!destination) return;
        const moved = columns[source.droppableId][source.index];
        if (moved?.row?.dataSource !== "CsvUploadAndManual") {
            NotificationManager.error(
                'You cannot move this reservation.',
                'Permission Denied',
                3000
            );
            return;
        }
        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) {
            return;
        }

        const startColumnId = source.droppableId;
        const endColumnId = destination.droppableId;

        const allowedTargets = ALLOWED_MOVES[startColumnId] || [];
        if (!allowedTargets.includes(endColumnId)) {
            NotificationManager.error(`A ${startColumnId} cannot be moved to ${endColumnId}. This status is automatically determined based on the dates.`, 'Action not allowed', 3000);
            return;
        }

        const startTasks = Array.from(columns[startColumnId]);
        const [movedTask] = startTasks.splice(source.index, 1);

        const endTasks = Array.from(columns[endColumnId]);
        endTasks.splice(destination.index, 0, movedTask);

        setColumns((prev) => ({
            ...prev,
            [startColumnId]: startTasks,
            [endColumnId]: endTasks,
        }));
        let newStatus = '';

        if (endColumnId === 'arrived') {
            newStatus = 'Arrived';
        } else if (endColumnId === 'departed') {
            newStatus = 'Departed';
        } else if (
            endColumnId === 'futureReservation' ||
            endColumnId === 'arrivingSoon'
        ){
            newStatus = 'confirmed';
        }

        try {
            await AxiosAPI({
                method: 'put',
                url: putAPI_GuestMessagingReservationStatus_Update,
                data: {
                    Id: movedTask.row.id,
                    Status: newStatus,
                    UpdatedBy: userName,
                },
            });
            onRefresh();
        } catch (err) {
            console.error('Status update failed', err);
        }
    };

    if (loading) {
        return (
            <Card className="d-flex flex-row mb-3">
                <CardBody>
                    <h4 style={{ textAlign: 'center' }}>Loading...</h4>
                </CardBody>
            </Card>
        );
    }

    return (
        <div className="reservation-kanban-wrapper">
            <style>{`
                .reservation-kanban-wrapper {
                    display:flex;
                    margin-top:20px;
                    justify-content:flex-start;
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px 24px;
                    border: 1px solid #e7e9ef;
                }
                .reservation-kanban-columns {
                    display: flex;
                    gap: 16px;
                    overflow-x: auto;
                }
                .reservation-kanban-column {
                    background: #ffffff;
                    border: 1px solid #d3d8e0;
                    border-radius: 16px;
                    padding: 16px;
                    min-width: 316px;
                    min-height: 520px;
                    box-shadow: 0 6px 18px rgba(20, 43, 63, 0.08);
                }
                .reservation-kanban-column.is-dragging-over {
                    border-color: var(--theme-color-1);
                }
                .reservation-kanban-column__title {
                    font-size: 16px;
                    font-weight: 600;
                    text-align:centre;
                    color: #143652;
                    margin-bottom: 14px;
                }
                .reservation-kanban-card {
                    position: relative;
                    background: #f6f8fa;
                    border-radius: 14px;
                    padding: 12px 14px;
                    border: 1px solid #c9d2dd;
                    margin-bottom: 12px;
                    text-align: centre;
                    transition: box-shadow 0.2s ease, transform 0.2s ease;
                }
                .reservation-kanban-card.is-dragging {
                    transform: rotate(1deg);
                    box-shadow: 0 12px 24px rgba(20, 43, 63, 0.18);
                }
                .reservation-kanban-card__guest {
                    font-weight: 600;
                    color: #12263f;
                    margin-bottom: 4px;
                }
                .reservation-kanban-card__date {
                    font-size: 13px;
                    color: #516074;
                    line-height: 1.3;
                }         
                .reservation-kanban-card__actions {
                    display:flex;
                    gap:8px;
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    opacity: 0;
                    transition: opacity 0.2s ease;
                }
                .reservation-kanban-card:hover .reservation-kanban-card__actions {
                    opacity: 1;
                }
                .reservation-kanban-card__edit-btn {
                    background: rgba(255,255,255,0.9);
                    border: 1px solid #d3d8e0;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                    cursor: pointer;
                    color: #143652;
                    transition: background 0.2s ease, color 0.2s ease;
                }
                .reservation-kanban-card__lock-btn {
                    background: rgba(255,255,255,0.9);
                    border: 1px solid #d3d8e0;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                    cursor: pointer;
                    color: #143652;
                    transition: background 0.2s ease, color 0.2s ease;
                }
                .reservation-kanban-card__edit-btn:hover {
                    background: var(--theme-color-1);
                    color: #ffffff;
                }
                .reservation-kanban-card__lock-btn:hover {
                    background: var(--theme-color-1);
                    color: #ffffff;
                }
                .reservation-kanban-column__empty {
                    border: 1px dashed #d3d8e0;
                    border-radius: 12px;
                    padding: 20px;
                    text-align: center;
                    font-size: 14px;
                    color: #8a94a6;
                    background: #fafdff;
                }
                    .reservation-kanban-card__tooltip {
                     position: absolute;
                     bottom: 42px;
                     left:8px;
                     padding: 1px 4px;
                     background:var(--theme-color-1);
                     color: #ffffff;
                     border-radius: 6px;
                     white-space: nowrap;
                     font-size: 10px;
                     opacity: 0;
                     transform: translateY(6px);
                     pointer-events: none;
                     transition: all 0.2s ease;
                    }
                 .reservation-kanban-card__lock-wrapper:hover .reservation-kanban-card__tooltip {
                    opacity: 1;
                    transform: translateY(0);
                    }
                .reservation-kanban-card__property {
                    font-size: 12px;
                    color: #6b7280;
                    margin-bottom: 4px;
                    font-weight: 500;
                    font-size: 12px;
                    color: #6b7280;
                    display: flex;
                    align-items: center;
                    margin-top: 2px;
                    gap: 1px;
                }
                .property-loader {
                    font-size: 12px;
                    display: flex;
                    color: #9ca3af;
                    gap: 6px;
                    align-items: center;
                }
                .spinning {
                   animation: spin 1s linear infinite;
                }

                @keyframes spin {
                   from { transform: rotate(0deg); }
                   to { transform: rotate(360deg); }
                }
                `
            }
            </style>

            <DragDropContext onDragEnd={onDragEnd}>
                <div className="reservation-kanban-columns">
                    {columnOrder.map(({ id, title }) => (
                        <Column
                            key={id}
                            columnId={id}
                            title={title}
                            tasks={columns[id] || []}
                            onRefresh={onRefresh}
                        />
                    ))}
                </div>
            </DragDropContext>

        </div>
    );
};

export default ReservationKanbanBoard;
